import React, { useState, useEffect, useCallback } from 'react';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { useTranslation } from 'react-i18next';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { apiService } from '../services/api.service';
import CustomFieldBuilder, { CustomField } from '../components/CustomFieldBuilder';
import RoleQuestionEditor, { CHAMP_ROLE_DEFAUT } from '../components/volunteers/RoleQuestionEditor';
import AvailabilityQuestionEditor, { eventDays, defaultAvailabilityField }
  from '../components/volunteers/AvailabilityQuestionEditor';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://logfit.eu';

interface Props {
  eventId: string;
  eventSlug?: string;
  embedded?: boolean;
}

export const VolunteerSettingsContent: React.FC<Props> = ({ eventId, eventSlug, embedded = false }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [introText, setIntroText] = useState('');
  const [fields, setFields] = useState<CustomField[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const baselineRef = React.useRef<((v: Record<string, any>) => void) | null>(null);

  const publicUrl = eventSlug ? `${PUBLIC_SITE_URL}/events/${eventSlug}/volunteers` : null;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.get(`/events/${eventId}/volunteer-settings`);
        const data = res.data || {};
        setIsOpen(!!data.is_open);
        setIntroText(data.intro_text || '');
        // Ce que le serveur porte : `commitIfChanged` s'y compare.
        baselineRef.current?.({ intro_text: data.intro_text || null });
        // Les jours de l'événement : ils DÉRIVENT des dates, l'organisateur
        // n'a rien à saisir — il choisit seulement comment il les regroupe.
        let jours: string[] = [];
        try {
          const ev = await apiService.get(`/events/${eventId}`);
          jours = eventDays(ev.data?.date_start, ev.data?.date_end);
        } catch {
          // Sans les dates, on n'invente pas de question de disponibilité.
        }
        setDays(jours);

        const champs = (data.custom_fields || []) as CustomField[];
        // La question de rôle est structurelle : un formulaire n'en manque
        // jamais (le serveur l'injecte aussi de son côté).
        let complets = champs.some((c) => c.is_role) ? champs : [CHAMP_ROLE_DEFAUT, ...champs];
        // La question de disponibilité existe dès qu'il y a un jour : depuis
        // les créneaux (2026-09-26), elle dit « matin ou après-midi » même sur
        // une seule journée. Sans date, elle n'a rien à proposer.
        if (jours.length >= 1 && !complets.some((c) => c.is_availability)) {
          complets = [...complets, defaultAvailabilityField(jours)];
        } else if (jours.length === 0) {
          complets = complets.filter((c) => !c.is_availability);
        }
        setFields(complets);
      } catch (err: any) {
        setError(err.response?.data?.detail || t('volunteers.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId, t]);

  /**
   * L'écran s'enregistre au fur et à mesure : l'interrupteur écrit tout de
   * suite, le texte d'introduction au blur, les questions à la validation de
   * leur propre fenêtre.
   *
   * ⚠️ Ça n'a été possible qu'après avoir rendu le `PUT` PARTIEL côté serveur :
   * il réécrivait la ligne entière, donc envoyer `is_open` seul effaçait le
   * texte et les questions.
   */
  const auto = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    await apiService.put(`/events/${eventId}/volunteer-settings`, patch);
    // Changer la question de rôle peut désaligner des candidatures déjà
    // reçues : on recompte pour que l'avertissement suive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]), {
    onError: (err: any) => setError(err?.response?.data?.detail || t('volunteers.saveError')),
  });
  baselineRef.current = auto.setBaseline;

  const handleCopy = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">{t('common.loading')}</div>;
  }

  const champRole = fields.find((f) => f.is_role) || CHAMP_ROLE_DEFAUT;
  const champDispo = fields.find((f) => f.is_availability);
  const autresChamps = fields.filter((f) => !f.is_role && !f.is_availability);
  const structurels = () => fields.filter((f) => f.is_role || f.is_availability);

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md bg-success/10 border border-success/30 px-4 py-3 text-sm text-success">
          {success}
        </div>
      )}

      {/* Ouverture des inscriptions */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="font-medium">{t('volunteers.isOpen')}</Label>
            <p className="text-xs text-muted-foreground">{t('volunteers.isOpenHint')}</p>
          </div>
          <Switch
            checked={isOpen}
            onCheckedChange={(v) => {
              setIsOpen(v);
              setError(null);
              void auto.commit({ is_open: v }, () => setIsOpen(!v));
            }}
          />
        </CardContent>
      </Card>

      {/* Lien public partageable */}
      {publicUrl && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <Label className="font-medium">{t('volunteers.publicLink')}</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-muted px-3 py-2 text-sm">{publicUrl}</code>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? <Check size={16} className="mr-1" /> : <Copy size={16} className="mr-1" />}
                {copied ? t('volunteers.copied') : t('volunteers.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, '_blank')}>
                <ExternalLink size={16} />
              </Button>
            </div>
            {!isOpen && (
              <p className="text-xs text-warning">{t('volunteers.closedWarning')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Texte d'intro */}
      <div className="space-y-2">
        <Label htmlFor="intro">{t('volunteers.introText')}</Label>
        <Textarea
          id="intro"
          rows={3}
          value={introText}
          onChange={(e) => setIntroText(e.target.value)}
          onBlur={(e) => void auto.commitIfChanged('intro_text', e.target.value || null)}
          placeholder={t('volunteers.introTextPlaceholder')}
        />
      </div>

      {/* Champs obligatoires — toujours demandés, à ne pas recréer en champ personnalisé */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('volunteers.fixedFieldsTitle')}</h3>
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{t('volunteers.firstName')}</Badge>
              <Badge variant="secondary">{t('volunteers.lastName')}</Badge>
              <Badge variant="secondary">{t('volunteers.email')}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{t('volunteers.fixedFieldsNote')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Question de rôle — structurelle, éditée à part : elle ne se supprime pas
          et chaque proposition porte la valeur qui alimentera le planning des juges. */}
      <RoleQuestionEditor
        field={champRole}
        onChange={(champ) => {
          const liste = fields.map((f) => (f.is_role ? champ : f));
          setFields(liste);
          setError(null);
          void auto.commit({ custom_fields: liste });
        }}
      />

      {/* Disponibilité — dès que l'événement a une date. */}
      {champDispo && days.length >= 1 && (
        <AvailabilityQuestionEditor
          field={champDispo}
          days={days}
          onChange={(champ) => {
            const liste = fields.map((f) => (f.is_availability ? champ : f));
            setFields(liste);
            setError(null);
            void auto.commit({ custom_fields: liste });
          }}
        />
      )}

      {/* Constructeur de champs (portée + options payantes masquées : sans objet pour des bénévoles) */}
      <CustomFieldBuilder
        fields={autresChamps}
        onChange={(autres) => {
          const liste = [...structurels(), ...autres];
          setFields(liste);
          setError(null);
          void auto.commit({ custom_fields: liste });
        }}
        showScope={false}
        showPaidOptions={false}
      />

      {/* L'écran s'enregistre tout seul : l'ÉTAT remplace le bouton. */}
      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <span className="text-xs text-muted-foreground">{t('autoSave.hint')}</span>
        <AutoSaveIndicator status={auto.status} retry={auto.retry} />
      </div>
    </div>
  );
};

export default VolunteerSettingsContent;
